/**
 * Utilitários globais de formatação numérica e máscaras de input.
 * Padrão: pt-BR (separador milhar = ponto, decimal = vírgula)
 *
 * REGRA DE USO:
 *  - Exibição de valores monetários  → formatCurrency()
 *  - Exibição de percentuais         → formatPercent()
 *  - Exibição de números simples     → formatNumber()
 *  - Máscaras de input durante digitação → maskCpf, maskCnpj, maskPhone, maskCep
 */

// ─── Formatadores de exibição ───────────────────────────────────────────────

const currencyFmt = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const percentFmt = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numberFmt = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formata um valor monetário no padrão brasileiro.
 * Ex: 1500 → "R$\u00a01.500,00"
 * Intl.NumberFormat usa R$\u00a0 — mantemos o comportamento nativo.
 */
export function formatCurrency(value: number | null | undefined): string {
  const v = value ?? 0;
  return currencyFmt.format(v);
}

/**
 * Formata um percentual com sempre 2 casas decimais.
 * Ex: 15.5 → "15,50%"
 */
export function formatPercent(value: number | null | undefined): string {
  const v = value ?? 0;
  return percentFmt.format(v) + "%";
}

/**
 * Formata um número simples com separador de milhar e 2 casas decimais.
 * Ex: 1500.42 → "1.500,42"
 */
export function formatNumber(value: number | null | undefined): string {
  const v = value ?? 0;
  return numberFmt.format(v);
}

// ─── Máscaras de input (durante digitação) ──────────────────────────────────

/** CEP: 00000-000 */
export function maskCep(value: string): string {
  const d = value.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/** CPF: 000.000.000-00 */
export function maskCpf(value: string): string {
  const d = value.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

/** CNPJ: 00.000.000/0000-00 */
export function maskCnpj(value: string): string {
  const d = value.replace(/\D/g, "").slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Telefone: (00) 00000-0000 */
export function maskPhone(value: string): string {
  const d = value.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

// ─── Validadores de documentos ──────────────────────────────────────────────

/** Valida CPF (11 dígitos + algoritmo de verificação) */
export function isValidCpf(cpf: string): boolean {
  const digits = cpf.replace(/\D/g, "");
  if (digits.length !== 11 || !!digits.match(/^(\d)\1+$/)) return false;
  const arr = digits.split("").map(Number);
  const calc = (n: number) => arr.slice(0, n).reduce((s, d, i) => s + d * (n + 1 - i), 0) * 10 % 11 % 10;
  return calc(9) === arr[9] && calc(10) === arr[10];
}

/** Valida CNPJ (14 dígitos + algoritmo de verificação) */
export function isValidCnpj(cnpj: string): boolean {
  const digits = cnpj.replace(/\D/g, "");
  if (digits.length !== 14 || !!digits.match(/^(\d)\1+$/)) return false;
  const calc = (n: number) => {
    const weights = n === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const sum = weights.reduce((s, w, i) => s + Number(digits[i]) * w, 0);
    const rev = sum % 11;
    return rev < 2 ? 0 : 11 - rev;
  };
  return calc(12) === Number(digits[12]) && calc(13) === Number(digits[13]);
}

// ─── Compat aliases (para não quebrar imports legados) ──────────────────────

/** @deprecated Use formatCurrency() */
export const fmtBRL = formatCurrency;

/** @deprecated Use formatPercent() */
export const fmtPercent = formatPercent;
